import { APP_URL, SCHOOL_INFO, USER_TOKEN, USER_ID, WECOM_CORP_ID } from "@/config/constants";

import { getBackUrl } from "@/utils/toolUtls";
import { clearSchoolYearFromStorage } from "@/utils/schoolYearUtils";
import { H5_TEACHER_GAOKAO_SCHOOL_GATE_SESSION } from "@/h5/constants/storageKeys";
import { requestSchoolUserLogout } from "@/utils/schoolUserLogout";

const GLOBAL_USER_TOKEN = "";

/** 跨产品共享 cookie 的分 Key 约定（与 land / 快系列对齐）。 */
export const BASE_TOKEN_COOKIE = {
  school: "baseToken.school",
  kuaicj: "baseToken.kuaicj",
  kuaipk: "baseToken.kuaipk",
  kuaijw: "baseToken.kuaijw",
  legacy: "baseToken",
} as const;

/** 智校退出时清理的 cookie 名（不含快系列产品 key）。 */
export const SCHOOL_BASE_TOKEN_COOKIE_KEYS = [
  BASE_TOKEN_COOKIE.school,
  BASE_TOKEN_COOKIE.legacy,
] as const;

export const LEGACY_USER_TOKEN_KEYS = [
  "jw_inland_user_token",
  "jw_intl_user_token",
  "kuaipk_user_token",
  "personal_user_token",
] as const;

export type TokenStorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payload] = token.split(".");
    if (!payload || typeof globalThis.atob !== "function") return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return JSON.parse(globalThis.atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** 三段且 payload 可解析 → 视为智校 JWT（非快系列 UUID memberToken）。 */
export function isLikelyJwt(token: string): boolean {
  const normalized = token?.trim();
  if (!normalized) return false;
  if (normalized.split(".").length !== 3) return false;
  return Boolean(decodeJwtPayload(normalized));
}

export function isStoredUserTokenExpired(token: string, now = Date.now()): boolean {
  if (!token.trim()) return true;
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return false;
  return now > payload.exp * 1000;
}

function isUsableSchoolAuthToken(token: string | null | undefined, now: number): token is string {
  const normalized = token?.trim();
  return Boolean(
    normalized && isLikelyJwt(normalized) && !isStoredUserTokenExpired(normalized, now),
  );
}

function readJwtSchoolId(token: string): string | null {
  const payload = decodeJwtPayload(token);
  if (
    !payload ||
    payload.schoolId === undefined ||
    payload.schoolId === null ||
    payload.schoolId === ""
  ) {
    return null;
  }
  return String(payload.schoolId);
}

/**
 * 解析可用 token。默认仍优先 cookie（活动页跨域回写）。
 * cookie 仅当为可用智校 JWT 时采纳（忽略快系列 UUID / 非 JWT）。
 * 若 cookie 与 localStorage 均可用且 JWT schoolId 不一致，以 localStorage 为准
 * （换校只写 storage、未同步 cookie 时的常见脏状态）。
 */
export function resolveStoredUserToken({
  cookieToken,
  canonicalToken,
  now = Date.now(),
}: {
  cookieToken?: string | null;
  canonicalToken?: string | null;
  now?: number;
}): string {
  const cookieOk = isUsableSchoolAuthToken(cookieToken, now);
  const canonicalOk = isUsableSchoolAuthToken(canonicalToken, now);
  if (cookieOk && canonicalOk) {
    const cookieSchool = readJwtSchoolId(cookieToken);
    const canonicalSchool = readJwtSchoolId(canonicalToken);
    if (cookieSchool && canonicalSchool && cookieSchool !== canonicalSchool) {
      return canonicalToken.trim();
    }
    return cookieToken.trim();
  }
  if (cookieOk) return cookieToken.trim();
  if (canonicalOk) return canonicalToken.trim();
  // storage 可能仍是历史 opaque；仅当像 JWT 时上面已覆盖。兼容：canonical 为未过期 JWT 已处理。
  // 若 canonical 是旧版非 JWT（极少），不采纳，避免与快系列 UUID 混淆。
  return "";
}

export function clearLegacyUserTokenStorage(storage: TokenStorageLike) {
  LEGACY_USER_TOKEN_KEYS.forEach((key) => storage.removeItem(key));
}

export function syncUserTokenStorage(storage: TokenStorageLike, token: string) {
  const normalized = token.trim();
  if (normalized) {
    storage.setItem(USER_TOKEN, normalized);
  } else {
    storage.removeItem(USER_TOKEN);
  }
  clearLegacyUserTokenStorage(storage);
}

export function saveUserToken(token: string) {
  syncUserTokenStorage(localStorage, token);
  // getUserToken 优先读 cookie：换校/登录只写 localStorage 会导致 Authorization 学校与 Redux 不一致。
  if (typeof document === "undefined") return;
  const normalized = token.trim();
  if (normalized) {
    setBaseToken2cookie(normalized);
  } else {
    removeBaseToken2Cookie();
  }
}

function getGlobalUserToken() {
  return GLOBAL_USER_TOKEN.trim();
}

function getCookieMap() {
  return document.cookie.split(";").reduce((map, item) => {
    const tokens = item.trim().split("=");
    const key = tokens.shift();
    if (key) map[key] = tokens.join("=");
    return map;
  }, {} as Record<string, string>);
}

function getEffectiveDomain() {
  return window.location.hostname.split(".").slice(-2).join(".");
}

function getBaseTokenCookieDomainCandidates(hostname: string): string[] {
  const normalized = hostname.trim().toLowerCase().replace(/\.$/, "");
  if (!normalized || !normalized.includes(".") || normalized.includes(":")) return [];
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(normalized)) return [];

  const parts = normalized.split(".").filter(Boolean);
  if (parts.length < 2) return [];

  const candidates = new Set<string>();
  for (let index = 0; index <= parts.length - 2; index += 1) {
    const domain = parts.slice(index).join(".");
    candidates.add(domain);
    candidates.add(`.${domain}`);
  }
  return Array.from(candidates);
}

function buildClearCookieDirectivesForKeys({
  hostname,
  protocol,
  keys,
}: {
  hostname: string;
  protocol: string;
  keys: readonly string[];
}): string[] {
  const secure = protocol === "https:";
  const directives: string[] = [];
  keys.forEach((key) => {
    directives.push(`${key}=;path=/;max-age=0`);
    getBaseTokenCookieDomainCandidates(hostname).forEach((domain) => {
      directives.push(`${key}=;domain=${domain};path=/;max-age=0`);
      if (secure) {
        directives.push(`${key}=;domain=${domain};path=/;max-age=0;Secure;SameSite=Lax`);
      }
    });
  });
  return directives;
}

/** 退出时删除智校 baseToken.school + 旧 baseToken（host-only + 各级父域）。 */
export function buildClearBaseTokenCookieDirectives({
  hostname,
  protocol,
}: {
  hostname: string;
  protocol: string;
}): string[] {
  return buildClearCookieDirectivesForKeys({
    hostname,
    protocol,
    keys: SCHOOL_BASE_TOKEN_COOKIE_KEYS,
  });
}

function writeCookieOnEffectiveDomain(name: string, value: string) {
  document.cookie = `${name}=${value};domain=${getEffectiveDomain()};path=/`;
}

// 设置智校会话到 cookie：写 baseToken.school，并双写旧 baseToken 兼容 land/旧包
export function setBaseToken2cookie(memberToken: string) {
  const normalized = memberToken.trim();
  if (!normalized) return;
  writeCookieOnEffectiveDomain(BASE_TOKEN_COOKIE.school, normalized);
  writeCookieOnEffectiveDomain(BASE_TOKEN_COOKIE.legacy, normalized);
}

// 移除智校 cookie（不清快系列产品 key）
export function removeBaseToken2Cookie() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const directives = buildClearBaseTokenCookieDirectives({
    hostname: window.location.hostname,
    protocol: window.location.protocol,
  });
  directives.forEach((directive) => {
    document.cookie = directive;
  });
}

function readCookieValue(name: string): string | undefined {
  const token = getCookieMap()[name];
  if (!token) return undefined;
  try {
    return decodeURIComponent(token);
  } catch {
    return token;
  }
}

// 从 cookie 获取智校 token：优先 .school，其次旧 baseToken（须为 JWT）
export function getBaseToken2cookie() {
  const school = readCookieValue(BASE_TOKEN_COOKIE.school);
  if (school && isLikelyJwt(school)) return school;
  const legacy = readCookieValue(BASE_TOKEN_COOKIE.legacy);
  if (legacy && isLikelyJwt(legacy)) return legacy;
  return undefined;
}

// 获取token
export function getUserToken() {
  const globalToken = getGlobalUserToken();
  if (globalToken) {
    syncUserTokenStorage(localStorage, globalToken);
    return globalToken;
  }
  const baseToken = getBaseToken2cookie();
  const canonicalToken = localStorage.getItem(USER_TOKEN);
  const token = resolveStoredUserToken({
    cookieToken: baseToken,
    canonicalToken,
  });

  if (token) {
    syncUserTokenStorage(localStorage, token);
    // 纠正「storage 新校 / cookie 旧校」后写回 cookie，避免下次又读到旧 JWT
    if (baseToken && token !== baseToken.trim()) {
      setBaseToken2cookie(token);
    } else if (baseToken && isStoredUserTokenExpired(baseToken)) {
      removeBaseToken2Cookie();
    }
    return token;
  }

  syncUserTokenStorage(localStorage, "");
  if (baseToken) removeBaseToken2Cookie();
  return null;
}

export function removeUserTokenWithRelation() {
  clearSchoolYearFromStorage();
  removeBaseToken2Cookie();

  syncUserTokenStorage(localStorage, "");
  localStorage.removeItem(USER_ID);
  localStorage.removeItem(SCHOOL_INFO);
  localStorage.removeItem(WECOM_CORP_ID);
  try {
    sessionStorage.removeItem(H5_TEACHER_GAOKAO_SCHOOL_GATE_SESSION);
  } catch {
    // ignore
  }
}

export function hasLoggedOutFlag(search: string): boolean {
  try {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    return params.get("logged_out") === "1";
  } catch {
    return false;
  }
}

export function consumeLoggedOutFlagFromUrl() {
  if (typeof window === "undefined") return false;
  if (!hasLoggedOutFlag(window.location.search)) return false;

  removeUserTokenWithRelation();

  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("logged_out");
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, "", nextUrl);
  } catch {
    // ignore
  }

  return true;
}

/**
 * 退出登录：清理登录态并以整页刷新跳转（落地 origin 取当前 window.location.origin）。
 * 必须带 query（logged_out=1）走 location.replace，避免同域仅改 hash 时 SPA 不刷新导致
 * Redux / 进行中请求把 token 写回。
 * redirectPath 仅作用于 PC 端落地页（默认新版应用中心 /basejw/appcenter?view=apps）；
 * H5 路由仍按现有规则跳对应登录页，不消费该参数。
 */
export async function logout(redirectPath = "/basejw/appcenter?view=apps") {
  const token = getUserToken();
  if (token) {
    await requestSchoolUserLogout(token);
  }

  removeUserTokenWithRelation();

  // 先清内存登录态，再跳转；防止仅 hash 切换时 UI 仍显示已登录。
  // 延迟 require，避免 baseTokenUtils ↔ store 在单测里形成加载环。
  try {
    const { setUserState } = require("@/store/user") as typeof import("@/store/user");
    setUserState({
      isLogin: false,
      hasInit: false,
      userInfo: undefined,
      school: undefined,
      currentSchoolYear: undefined,
    });
  } catch {
    // 非浏览器或 store 未就绪时忽略
  }

  // 退出落地必须留在当前访问域（如 bsjw.basejy.com），禁止用写死的 APP_URL 跨到 school.basejy.com
  const logoutOrigin =
    typeof window !== "undefined" && window.location?.origin ? window.location.origin : APP_URL;

  // 判断当前是否是 H5 路由
  const isH5Route = window.location.hash.includes("/h5");

  const backUrl = encodeURIComponent(getBackUrl());

  if (isH5Route) {
    // H5 路由根据当前路径判断跳转到对应的登录页
    const currentPath = window.location.hash.replace(/^#/, "");
    let loginPath = "/h5/login";

    // 根据当前路径判断应该跳转到哪个登录页
    if (currentPath.startsWith("/h5/teacher") || currentPath.startsWith("/h5/login/teacher")) {
      loginPath = "/h5/login/teacher";
    } else if (currentPath.startsWith("/h5/parent") || currentPath.startsWith("/h5/login/parent")) {
      loginPath = "/h5/login/parent";
    } else if (backUrl) {
      // 根据 redirect 参数判断
      try {
        const decodedBackUrl = decodeURIComponent(backUrl);
        if (decodedBackUrl.startsWith("/h5/teacher")) {
          loginPath = "/h5/login/teacher";
        } else if (decodedBackUrl.startsWith("/h5/parent")) {
          loginPath = "/h5/login/parent";
        }
      } catch (e) {
        // 解析失败，使用默认登录页
      }
    }

    window.location.replace(`${logoutOrigin}/?logged_out=1#${loginPath}?redirect=${backUrl}`);
    return;
  }

  // 统一登出后返回落地页（默认新版应用中心，个人中心等入口可指定 path）
  window.location.replace(`${logoutOrigin}/?logged_out=1#${redirectPath}`);
}

export function saveUserBasicsToStorage(data?: {
  schoolUserInfo?: { userId?: string | number | null; username?: string | null };
  schoolInfo?: {
    schoolId?: string | number | null;
    schoolName?: string | null;
    avatar?: string | null;
    displayMode?: number | null;
  };
}) {
  if (!data) return;
  const { schoolUserInfo, schoolInfo } = data;

  const userIdentifier =
    schoolUserInfo?.userId ??
    (schoolUserInfo?.username !== undefined && schoolUserInfo?.username !== null
      ? schoolUserInfo.username
      : undefined);
  if (userIdentifier !== undefined && userIdentifier !== null) {
    localStorage.setItem(USER_ID, String(userIdentifier));
  }

  if (schoolInfo) {
    const payload = {
      id: schoolInfo.schoolId ?? null,
      name: schoolInfo.schoolName ?? null,
      avatar: schoolInfo.avatar ?? null,
      displayMode: schoolInfo.displayMode ?? null,
    };
    localStorage.setItem(SCHOOL_INFO, JSON.stringify(payload));
  }
}
